<template>
  <v-dialog
    :value="value"
    :max-width="520"
    persistent
    :attach="attach"
    content-class="prr-iq-suggest"
    @keydown.esc="requestClose"
  >
    <div class="prr-iq-suggest__card">
      <div class="prr-iq-suggest__header">
        <div class="prr-iq-suggest__header-text">
          <span class="prr-iq-suggest__title">IQ Suggest</span>
          <span v-if="positionName" class="prr-iq-suggest__subtitle">{{ positionName }}</span>
        </div>
        <button
          type="button"
          class="prr-iq-suggest__close-btn"
          aria-label="Close IQ Suggest"
          @click="requestClose"
        >
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div v-if="isDone" class="prr-iq-suggest__done" data-testid="iq-suggest-done">
        <div class="prr-iq-suggest__done-icon" aria-hidden="true">
          <v-icon large color="#12B76A">mdi-check-circle</v-icon>
        </div>
        <span class="prr-iq-suggest__done-title">All suggestions reviewed</span>
        <span class="prr-iq-suggest__done-body">
          {{ acceptedIds.length }} accepted suggestion{{ acceptedIds.length === 1 ? "" : "s" }} will
          stay attached.
        </span>
        <StandardButton
          size="small"
          type="magenta"
          variant="primary"
          label="Close"
          @click="finish"
        />
      </div>

      <div
        v-else-if="currentSuggestion"
        class="prr-iq-suggest__review"
        data-testid="iq-suggest-review"
      >
        <div class="prr-iq-suggest__progress">
          Reviewing {{ currentIndex + 1 }} of {{ suggestions.length }}
        </div>
        <div class="prr-iq-suggest__suggestion">
          <OrgObjectCard
            :type="currentSuggestion.type"
            :code-value="currentSuggestion.code"
            :name="currentSuggestion.name"
            :icon="true"
            :allow_hover="false"
            :show-tooltip="true"
          />
          <OrgObjectStatusBadge :status="currentSuggestion.status" />
        </div>
        <p class="prr-iq-suggest__hint">
          Accept to attach this suggestion to the position, or dismiss it to review the next one.
        </p>
        <div class="prr-iq-suggest__actions">
          <StandardButton
            size="small"
            type="magenta"
            variant="secondary"
            label="Dismiss"
            @click="dismissCurrent"
          />
          <StandardButton
            size="small"
            type="magenta"
            variant="primary"
            label="Accept"
            @click="acceptCurrent"
          />
        </div>
      </div>

      <IQSuggestOverlayExitConfirmDialog
        :value="showExitConfirm"
        :suggestions-remaining="remaining"
        :attach="attach"
        @input="showExitConfirm = $event"
        @keep-reviewing="keepReviewing"
        @confirm-exit="confirmExit"
      />
    </div>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import OrgObjectCard from "@components/OrgObjectCard/OrgObjectCard.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import IQSuggestOverlayExitConfirmDialog from "@components/IQSuggestOverlayExitConfirmDialog/IQSuggestOverlayExitConfirmDialog.vue";

const props = defineProps({
  value: { type: Boolean, default: false },
  suggestions: { type: Array, default: () => [] },
  positionName: { type: String, default: "" },
  attach: { type: String, default: undefined },
});

const emit = defineEmits(["input", "accept", "dismiss", "exit-attempt", "exit-confirmed"]);

const currentIndex = ref(0);
const acceptedIds = ref([]);
const showExitConfirm = ref(false);

const currentSuggestion = computed(() => props.suggestions[currentIndex.value] || null);
const total = computed(() => props.suggestions.length);
const remaining = computed(() => Math.max(0, total.value - currentIndex.value));
const isDone = computed(() => total.value > 0 && currentIndex.value >= total.value);

watch(
  () => props.value,
  (open) => {
    if (open) {
      currentIndex.value = 0;
      acceptedIds.value = [];
      showExitConfirm.value = false;
    }
  },
  { immediate: true }
);

function acceptCurrent() {
  if (!currentSuggestion.value) return;
  acceptedIds.value = [...acceptedIds.value, currentSuggestion.value.id];
  emit("accept", currentSuggestion.value);
  currentIndex.value += 1;
}

function dismissCurrent() {
  if (!currentSuggestion.value) return;
  emit("dismiss", currentSuggestion.value);
  currentIndex.value += 1;
}

function requestClose() {
  if (remaining.value > 0 && !isDone.value) {
    emit("exit-attempt", { suggestionsRemaining: remaining.value });
    showExitConfirm.value = true;
    return;
  }
  finish();
}

function keepReviewing() {
  showExitConfirm.value = false;
}

function confirmExit() {
  showExitConfirm.value = false;
  emit("exit-confirmed");
  emit("input", false);
}

function finish() {
  showExitConfirm.value = false;
  emit("input", false);
}
</script>

<style scoped>
.prr-iq-suggest__card {
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 20px 24px rgba(16, 24, 40, 0.12);
}
.prr-iq-suggest__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}
.prr-iq-suggest__header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.prr-iq-suggest__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}
.prr-iq-suggest__subtitle {
  font-size: 14px;
  line-height: 20px;
  color: #667085;
}
.prr-iq-suggest__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #98a2b3;
}
.prr-iq-suggest__progress {
  font-size: 13px;
  font-weight: 500;
  color: #475467;
  margin-bottom: 12px;
}
.prr-iq-suggest__suggestion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fcfcfd;
}
.prr-iq-suggest__hint {
  margin: 12px 0 16px;
  font-size: 14px;
  line-height: 20px;
  color: #667085;
}
.prr-iq-suggest__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.prr-iq-suggest__done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0 8px;
  text-align: center;
}
.prr-iq-suggest__done-title {
  font-size: 16px;
  font-weight: 600;
  color: #101828;
}
.prr-iq-suggest__done-body {
  font-size: 14px;
  color: #667085;
  margin-bottom: 8px;
}
</style>
